import React, { useEffect, useState } from 'react';
import { Box, Container, Paper, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,  } from '@mui/material';
import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import { toast } from 'react-toastify';
import NotificationModal from './modals/notifications/notificationModal';
import DeleteNotificationModal from './modals/notifications/deleteNotificationModal';
import ClassificationBannerHeader from '../common/banner/classifactionBannerHeader';
import PageHeader from '../common/banner/pageHeader';
import { sortAdminItems } from '../common/utils/tableUtil';
import SearchBar from '../common/search/SearchBar';

const NotificationsTable = ({ items, onToggle, sortConfig, onSortChange, fetchNotifications, setNotifications }) => {

  const handleUpdate = (notification) =>  {    
    toast.success(`${notification.name} Updated`, { autoClose: 3000 });
    fetchNotifications();
  };


  const handleDelete = (notification) =>  {
    setNotifications(items.filter(items => items.id !== notification.id));
    toast.success(`${notification.name} Deleted`, { autoClose: 3000 });
  };

  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ key, direction });
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.key !== field) return null;
    return sortConfig.direction === 'asc' ? (
      <ArrowDropUp fontSize="small" />
    ) : (
      <ArrowDropDown fontSize="small" />
    );
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 4 }}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">    
          <TableHead>
            <TableRow sx={{ backgroundColor: '#0568AE' }}>
              {['Name', 'Notification', 'Enabled'].map((field) => (
                <TableCell
                  key={field}
                  onClick={() => handleSort(field)}
                  sx={{ color: '#EEEEEE', fontWeight: 'bold', cursor: 'pointer', notificationSelect: 'none' }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>{field.charAt(0).toUpperCase() + field.slice(1)}</span>
                    <SortIcon field={field} />
                  </Stack>
                </TableCell>                
              ))}
              <TableCell sx={{ color: '#EEEEEE', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={item.id} sx={{ height: 56, backgroundColor: index % 2 === 0 ? '#2A2A2A' : '#000000' }}>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.name}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.description}</TableCell>
                <TableCell> <Switch checked={!!item.enabled} onChange={(e) => onToggle(item, 'enabled', e.target.checked)} /> </TableCell>
                <TableCell>
                    <Stack direction="row" spacing={1}>
                        <NotificationModal notification={ item } onTriggerUpdate={handleUpdate} />
                        <DeleteNotificationModal notification={ item } onTriggerDelete={handleDelete} />
                    </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

export default function Notifications() {
    const [notifications, setNotifications] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        fetchNotifications();
    }, []);

    const fetchNotifications = async () => {
        try {
                
            const response = await fetch(`${process.env.REACT_APP_API}/notifications/`);            
            if (!response.ok) {
                toast.error(`HTTP error! status: ${response.status}`);
            } else {
                const json = await response.json();
                setNotifications(json.data);
            }
        } catch (error) {
            toast.error(error);
        } 
    };

    const handleCreate = (notification) =>  {
      toast.success(`${notification.name} Created`, { autoClose: 3000 });
      fetchNotifications();
    };


    const handleUpdate = async (notification) => {
         const res = await fetch(`${process.env.REACT_APP_API}/notifications/update`, {
             method: 'PUT',
             headers: { 'Content-Type': 'application/json' },
             body: JSON.stringify(notification)
         });
         if (!res.ok) {
            toast.error(`Failed to update notification : ${res.status}`);
          } else {
            setNotifications((prev) => prev.map((p) => (p.id === notification.id ? notification : p)));
         
            if ( notification.enabled ) {
              toast.success(`${notification.name} Notification Has Been Enabled`, { autoClose: 3000 });
            } else {
              toast.success(`${notification.name} Notification Has Been Disabled`, { autoClose: 3000 });
            }
            
          }
     };

    const handleSortChange = (newConfig) => {
        setSortConfig(newConfig);
    };

    const filtered = notifications?.filter((item) => {
        const s = searchQuery.toLowerCase();
        return ['name', 'description', 'enabled'].some((field) => item[field]?.toLowerCase().includes(s));
    });

  const sorted = sortAdminItems(filtered, sortConfig);
  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Container sx={{ minWidth:1800, mt: 10, mb: 10 }}>
        <Box sx={{pt: 2}}>
            <ClassificationBannerHeader />
            <PageHeader title="Notifications" />
            <Stack direction="row" justifyContent="flex-end" mt={2} mb={1} sx={{ width: '100%', mx: 'auto' }}>                    
                  <SearchBar
                      value={searchQuery}
                      onChange={setSearchQuery}
                      placeholder="Search Notifications..."
                      label="Search Notifications..."
                      width={200}
                  />                
            </Stack>
          
            <TableContainer component={Paper} sx={{ mt: 4, backgroundColor: '#1A1A1A'  }}>
                <NotificationsTable
                    items={paginated}
                    onToggle={(item, key, value) => handleUpdate({ ...item, [key]: value })}
                    sortConfig={sortConfig}
                    onSortChange={handleSortChange}
                    fetchNotifications={fetchNotifications}
                    setNotifications={setNotifications}
                />
                <TablePagination
                    component="div"
                    count={sorted.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    sx={{ color: 'white', '& .MuiSvgIcon-root': { color: 'white' } }}
                />
            </TableContainer>
            <NotificationModal notification='' onTriggerCreate={handleCreate} />
        </Box>
    </Container>
  )
}
